import { useParams } from 'react-router-dom'

import { useGetByTenantAccountAndAccountSourceAccountIdentifierQuery } from '@/api'
import { Card, LabelValueRow, Tabs } from '@/components/core-v2'
import { TENANT } from '@/const'
import { Currency, formatCurrencyFull } from '@/utilities/currency'

import { Loading } from './loading'

export const TradeRiskDetailsTab = () => {
  const { source, tradeId } = useParams()
  const { data: accountData, isLoading: accountIsLoading } =
    useGetByTenantAccountAndAccountSourceAccountIdentifierQuery({
      tenant: TENANT,
      accountIdentifier: tradeId,
      accountSource: source
    })

  if (accountIsLoading) return <Loading />

  const { account, accountSnapShots } = accountData

  const latestSnapshot = accountSnapShots[accountSnapShots.length - 1]

  const hasProtection = account.strategy.type === 'TIPP' || account.strategy.type === 'CPPI'

  const loanAmount = latestSnapshot.loanHoldings.reduce((acc, holding) => holding.value + acc, 0)
  const loanAllocation = latestSnapshot.loanAllocation || loanAmount
  const currency = latestSnapshot.notionalCurrency as Currency

  const checkValue =
    latestSnapshot.targetMultiplier &&
    formatCurrencyFull(
      (1 / latestSnapshot.targetMultiplier) * (latestSnapshot.performanceAllocation || 0) +
        (latestSnapshot.protectionAllocation || 0) -
        loanAllocation,
      currency
    )
  const grossTradeValue = formatCurrencyFull(latestSnapshot.accountValue + loanAllocation, currency)
  const netTradeValue = formatCurrencyFull(latestSnapshot.accountValue, currency)
  const loanAmountValue = formatCurrencyFull(loanAllocation, currency)
  const portfolioValue =
    latestSnapshot.targetMultiplier &&
    latestSnapshot.targetMultiplier.toLocaleString(undefined, {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2
    })
  const impliedValue = latestSnapshot.impliedMultiplier
    ? latestSnapshot.impliedMultiplier.toLocaleString(undefined, {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2
      })
    : null
  const riskTargetAllocationValue =
    latestSnapshot.targetPerformanceAllocation &&
    formatCurrencyFull(latestSnapshot.targetPerformanceAllocation, currency)
  const riskCurrentAllocationValue = formatCurrencyFull(
    (latestSnapshot.performanceAllocation || 0) +
      (latestSnapshot.pendingPerformanceAllocation || 0),
    currency
  )
  const deltaValue =
    latestSnapshot.impliedMultiplier &&
    (-1 * (1 / latestSnapshot.impliedMultiplier)).toLocaleString(undefined, {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
      style: 'percent'
    })
  const protectedAmountValue = formatCurrencyFull(latestSnapshot.protectedAmount, currency)

  const isCppi = account.strategy.type === 'CPPI'
  const valueToMinus = isCppi ? latestSnapshot.bondFloor : latestSnapshot.protectedAmount
  const cushionValue = formatCurrencyFull(latestSnapshot.accountValue - valueToMinus, currency)
  const impliedPortfolioValue =
    latestSnapshot.targetMultiplier &&
    (latestSnapshot.impliedMultiplier / latestSnapshot.targetMultiplier).toLocaleString(undefined, {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
      style: 'percent'
    })
  const safeTargetAllocationValue =
    latestSnapshot.targetProtectionAllocation &&
    formatCurrencyFull(latestSnapshot.targetProtectionAllocation, currency)
  const safeCurrentAllocationValue = formatCurrencyFull(
    (latestSnapshot.protectionAllocation || 0) + (latestSnapshot.pendingProtectionAllocation || 0),
    currency
  )
  const currentlyRebalancingValue = latestSnapshot.operations.some(op => op.type === 'Rebalancing')
    ? 'Yes'
    : 'No'

  return (
    <Tabs.Panel value="details">
      <Card.Wrapper className="mt-10">
        {hasProtection && <LabelValueRow label="Check" value={checkValue} />}
        <LabelValueRow label="Gross trade value" value={grossTradeValue} />
        <LabelValueRow label="Net trade value" value={netTradeValue} />
        <LabelValueRow label="Leverage amount" value={loanAmountValue} />
        {hasProtection && (
          <>
            <LabelValueRow label="Portfolio multiplier" value={portfolioValue} />
            <LabelValueRow label="Implied multiplier" value={impliedValue} />
            <LabelValueRow
              label="Performance asset (target allocation)"
              value={riskTargetAllocationValue}
            />
            <LabelValueRow
              label="Performance asset (current allocation)"
              value={riskCurrentAllocationValue}
            />
            <LabelValueRow label="Delta" value={deltaValue} />
            <LabelValueRow label="Cushion" value={cushionValue} />
            <LabelValueRow label="Protected value" value={protectedAmountValue} />
            <LabelValueRow
              label="Multiplier ratio (Implied / Portfolio)"
              value={impliedPortfolioValue}
            />
            <LabelValueRow
              label="Protection asset (target allocation)"
              value={safeTargetAllocationValue}
            />
            <LabelValueRow
              label="Protection asset (current allocation)"
              value={safeCurrentAllocationValue}
            />
            <LabelValueRow
              showBottomBorder
              label="Currently rebalancing"
              value={currentlyRebalancingValue}
            />
          </>
        )}
      </Card.Wrapper>
    </Tabs.Panel>
  )
}
